/* ==========================================================================
   HK CREATION — storefront interactions
   Self-contained: quick add to cart, wishlist, sticky mobile CTA, toast.
   No dependency on Dawn's internals beyond window.routes, so it keeps working
   if the base theme is updated.
   ========================================================================== */
(function () {
  'use strict';

  var routes = window.routes || {};
  var CART_ADD = routes.cart_add_url || '/cart/add';
  var CART_URL = routes.cart_url || '/cart';
  var WISHLIST_KEY = 'hk:wishlist';

  /* ---------------------------------------------------------------------
     Toast
     --------------------------------------------------------------------- */
  var toastEl = null;
  var toastTimer = null;

  function buildToast() {
    if (toastEl) return toastEl;
    toastEl = document.createElement('div');
    toastEl.className = 'hk-toast';
    toastEl.setAttribute('role', 'status');
    toastEl.setAttribute('aria-live', 'polite');
    toastEl.innerHTML =
      '<svg class="hk-toast__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" ' +
      'stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M20 6 9 17l-5-5"/></svg>' +
      '<div class="hk-toast__body"></div>' +
      '<button type="button" class="hk-toast__close" aria-label="Close">&times;</button>';
    toastEl.querySelector('.hk-toast__close').addEventListener('click', hideToast);
    document.body.appendChild(toastEl);
    return toastEl;
  }

  function hideToast() {
    if (toastEl) toastEl.setAttribute('data-visible', 'false');
  }

  function showToast(title, message, opts) {
    opts = opts || {};
    var el = buildToast();
    var icon = el.querySelector('.hk-toast__icon');
    icon.classList.toggle('hk-toast__icon--error', !!opts.error);
    icon.innerHTML = opts.error
      ? '<circle cx="12" cy="12" r="9"/><path d="M12 8v5"/><path d="M12 16h.01"/>'
      : '<path d="M20 6 9 17l-5-5"/>';

    var body = el.querySelector('.hk-toast__body');
    body.innerHTML = '';

    var strong = document.createElement('strong');
    strong.textContent = title;
    body.appendChild(strong);

    if (message) {
      var p = document.createElement('p');
      p.textContent = message;
      body.appendChild(p);
    }

    if (opts.link) {
      var a = document.createElement('a');
      a.href = opts.link.href;
      a.textContent = opts.link.label;
      body.appendChild(a);
    }

    el.setAttribute('data-visible', 'true');
    clearTimeout(toastTimer);
    toastTimer = setTimeout(hideToast, opts.error ? 6000 : 4500);
  }

  /* ---------------------------------------------------------------------
     Cart count bubble — keep Dawn's header badge honest after an AJAX add
     --------------------------------------------------------------------- */
  function refreshCartCount() {
    return fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        document.querySelectorAll('.cart-count-bubble').forEach(function (bubble) {
          var span = bubble.querySelector('span[aria-hidden="true"]') || bubble.querySelector('span');
          if (span) span.textContent = cart.item_count;
          bubble.hidden = cart.item_count === 0;
        });
        var iconBubbleHost = document.querySelector('#cart-icon-bubble');
        if (iconBubbleHost && !iconBubbleHost.querySelector('.cart-count-bubble') && cart.item_count > 0) {
          var b = document.createElement('div');
          b.className = 'cart-count-bubble';
          b.innerHTML = '<span aria-hidden="true">' + cart.item_count + '</span>';
          iconBubbleHost.appendChild(b);
        }
        document.dispatchEvent(new CustomEvent('hk:cart:updated', { detail: cart }));
        return cart;
      })
      .catch(function () { /* count refresh is cosmetic — never block the add */ });
  }

  /* ---------------------------------------------------------------------
     Quick add
     --------------------------------------------------------------------- */
  function handleQuickAdd(button) {
    var variantId = button.getAttribute('data-variant-id');
    if (!variantId || button.getAttribute('data-loading') === 'true') return;

    var title = button.getAttribute('data-product-title') || 'Item';
    button.setAttribute('data-loading', 'true');
    button.setAttribute('aria-busy', 'true');

    fetch(CART_ADD + '.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: [{ id: Number(variantId), quantity: 1 }] })
    })
      .then(function (response) {
        return response.json().then(function (data) {
          if (!response.ok) throw new Error(data.description || data.message || 'Could not add to cart');
          return data;
        });
      })
      .then(function () {
        return refreshCartCount();
      })
      .then(function () {
        showToast('Added to bag', title, { link: { href: CART_URL, label: 'View bag' } });
      })
      .catch(function (error) {
        showToast('Could not add to bag', error.message, { error: true });
      })
      .then(function () {
        button.setAttribute('data-loading', 'false');
        button.removeAttribute('aria-busy');
      });
  }

  /* ---------------------------------------------------------------------
     Wishlist — persisted per browser in localStorage
     --------------------------------------------------------------------- */
  function readWishlist() {
    try {
      var raw = window.localStorage.getItem(WISHLIST_KEY);
      var parsed = raw ? JSON.parse(raw) : [];
      return Array.isArray(parsed) ? parsed : [];
    } catch (e) {
      return [];
    }
  }

  function writeWishlist(list) {
    try {
      window.localStorage.setItem(WISHLIST_KEY, JSON.stringify(list));
    } catch (e) {
      /* private mode or blocked storage — the toggle still works for this page */
    }
  }

  function syncWishlistButtons() {
    var list = readWishlist();
    document.querySelectorAll('.hk-wish').forEach(function (button) {
      var handle = button.getAttribute('data-handle');
      var saved = list.indexOf(handle) !== -1;
      button.setAttribute('aria-pressed', saved ? 'true' : 'false');
      var label = button.querySelector('.hk-visually-hidden');
      if (label) {
        label.textContent = (saved ? 'Remove ' : 'Save ') + (button.getAttribute('data-title') || 'product') +
          (saved ? ' from your wishlist' : ' to your wishlist');
      }
    });
  }

  function toggleWishlist(button) {
    var handle = button.getAttribute('data-handle');
    if (!handle) return;
    var list = readWishlist();
    var index = list.indexOf(handle);
    var added = index === -1;

    if (added) list.push(handle);
    else list.splice(index, 1);

    writeWishlist(list);
    syncWishlistButtons();
    showToast(
      added ? 'Saved to wishlist' : 'Removed from wishlist',
      button.getAttribute('data-title') || ''
    );
  }

  /* ---------------------------------------------------------------------
     Sticky mobile CTA — reveal once the hero has scrolled out of view
     --------------------------------------------------------------------- */
  function initStickyBar() {
    var bar = document.querySelector('.hk-sticky');
    if (!bar) return;

    document.body.classList.add('hk-has-sticky');

    var sentinel = document.querySelector('.hk-hero') || document.querySelector('[data-hk-sticky-anchor]');
    if (!sentinel || !('IntersectionObserver' in window)) {
      bar.setAttribute('data-visible', 'true');
      return;
    }

    new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          bar.setAttribute('data-visible', entry.isIntersecting ? 'false' : 'true');
        });
      },
      { rootMargin: '-40% 0px 0px 0px', threshold: 0 }
    ).observe(sentinel);
  }

  /* ---------------------------------------------------------------------
     Rails — keyboard and pointer affordances
     --------------------------------------------------------------------- */
  function initRails() {
    document.querySelectorAll('.hk-rail').forEach(function (rail) {
      // A scrollable region needs to be focusable and labelled for keyboard and
      // screen reader users, but only while it is actually scrollable.
      var update = function () {
        var scrollable = rail.scrollWidth > rail.clientWidth + 4;
        if (scrollable) {
          rail.setAttribute('tabindex', '0');
          rail.setAttribute('role', 'region');
          if (!rail.hasAttribute('aria-label')) rail.setAttribute('aria-label', 'Scrollable list');
        } else {
          rail.removeAttribute('tabindex');
          rail.removeAttribute('role');
        }
      };
      update();
      window.addEventListener('resize', update, { passive: true });
    });
  }

  /* ---------------------------------------------------------------------
     Product page — sticky buy bar
     Mirrors the real Dawn product form rather than duplicating it, so the
     variant it adds is always the one the customer has selected.
     --------------------------------------------------------------------- */
  function productForm() {
    return document.querySelector('product-info form[action*="/cart/add"]')
      || document.querySelector('form[action*="/cart/add"]:not([data-hk-ignore])');
  }

  function currentVariantId() {
    var form = productForm();
    if (!form) return null;
    var input = form.querySelector('input[name="id"]');
    return input && input.value ? input.value : null;
  }

  function currentPriceText() {
    var price = document.querySelector('product-info .price');
    if (!price) return '';
    var node = price.classList.contains('price--on-sale')
      ? price.querySelector('.price__sale .price-item--sale')
      : price.querySelector('.price__regular .price-item--regular');
    node = node || price.querySelector('.price-item');
    return node ? node.textContent.trim() : '';
  }

  function mainSubmitDisabled() {
    var submit = document.querySelector('product-info .product-form__submit');
    return !!(submit && submit.hasAttribute('disabled'));
  }

  function initBuyBar() {
    var bar = document.querySelector('.hk-buybar');
    if (!bar) return;

    document.body.classList.add('hk-has-buybar');

    var addBtn = bar.querySelector('[data-hk-buybar-add]');
    var priceEl = bar.querySelector('.hk-buybar__price');
    var picker = document.querySelector('product-info variant-selects')
      || document.querySelector('product-info .product-form__input');
    var multiVariant = bar.getAttribute('data-multi-variant') === 'true';
    var chosen = !multiVariant;

    function sync() {
      if (priceEl) priceEl.textContent = currentPriceText();
      if (!addBtn) return;
      var soldOut = mainSubmitDisabled();
      addBtn.disabled = soldOut;
      var label = addBtn.querySelector('[data-hk-buybar-label]');
      if (label) {
        label.textContent = soldOut
          ? 'Sold out'
          : (multiVariant && !chosen ? bar.getAttribute('data-options-label') || 'Choose options'
                                     : bar.getAttribute('data-add-label') || 'Add to bag');
      }
    }

    // Dawn re-renders the price and the hidden variant input on every variant
    // change, so watch the subtree rather than guessing at its event names.
    var host = document.querySelector('product-info');
    if (host && 'MutationObserver' in window) {
      new MutationObserver(function () { sync(); }).observe(host, {
        childList: true, subtree: true, characterData: true, attributes: true,
        attributeFilter: ['disabled', 'value', 'class']
      });
      host.addEventListener('change', function () { chosen = true; sync(); });
    }

    // Reveal only once the real add-to-cart button has scrolled away.
    var anchor = document.querySelector('product-info .product-form__buttons');
    if (anchor && 'IntersectionObserver' in window) {
      new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          bar.setAttribute('data-visible', entry.isIntersecting ? 'false' : 'true');
        });
      }, { threshold: 0 }).observe(anchor);
    } else {
      bar.setAttribute('data-visible', 'true');
    }

    if (addBtn) {
      addBtn.addEventListener('click', function (event) {
        event.preventDefault();
        if (addBtn.disabled) return;

        // First tap on a product with options sends the customer up to choose,
        // so nobody buys the default size without noticing.
        if (multiVariant && !chosen) {
          chosen = true;
          if (picker) {
            picker.scrollIntoView({ behavior: 'smooth', block: 'center' });
            picker.classList.add('hk-flash');
            setTimeout(function () { picker.classList.remove('hk-flash'); }, 2400);
          }
          sync();
          return;
        }

        var id = currentVariantId();
        if (!id) {
          var form = productForm();
          if (form) form.submit();
          return;
        }
        addBtn.setAttribute('data-variant-id', id);
        addBtn.setAttribute('data-product-title', bar.getAttribute('data-product-title') || '');
        handleQuickAdd(addBtn);
      });
    }

    sync();
  }

  /* ---------------------------------------------------------------------
     Product page — mobile gallery counter
     --------------------------------------------------------------------- */
  function initGalleryCounter() {
    var list = document.querySelector('media-gallery .product__media-list');
    if (!list) return;

    var slides = list.querySelectorAll('.product__media-item');
    if (slides.length < 2) return;

    var wrapper = document.querySelector('.product__media-wrapper');
    if (!wrapper) return;

    var counter = wrapper.querySelector('.hk-gallery-counter');
    if (!counter) {
      counter = document.createElement('p');
      counter.className = 'hk-gallery-counter';
      counter.setAttribute('aria-hidden', 'true');
      // Sits directly under the slider, above Dawn's thumbnail rail.
      var slider = wrapper.querySelector('slider-component');
      if (slider && slider.parentNode) slider.parentNode.insertBefore(counter, slider.nextSibling);
      else wrapper.appendChild(counter);
    }

    function render() {
      // Slide width, not container width: Dawn's peek layout makes each slide
      // narrower than the viewport, so clientWidth would drift out of step.
      var step = slides[0].offsetWidth;
      if (slides.length > 1) {
        var delta = slides[1].offsetLeft - slides[0].offsetLeft;
        if (delta > 0) step = delta;
      }
      var index = step > 0 ? Math.round(list.scrollLeft / step) : 0;
      index = Math.max(0, Math.min(index, slides.length - 1));
      counter.innerHTML = '<b>' + (index + 1) + '</b> / ' + slides.length;
    }

    render();
    list.addEventListener('scroll', function () {
      window.requestAnimationFrame(render);
    }, { passive: true });
    window.addEventListener('resize', render, { passive: true });
  }

  /* ---------------------------------------------------------------------
     Tabbed product sections
     A real tablist: arrow keys move between tabs, Home/End jump to the ends,
     and the panels are plain hidden/shown so nothing reloads.
     --------------------------------------------------------------------- */
  function initTabs() {
    document.querySelectorAll('[data-hk-tabs]').forEach(function (list) {
      if (list.getAttribute('data-hk-tabs-ready') === 'true') return;
      list.setAttribute('data-hk-tabs-ready', 'true');

      var tabs = Array.prototype.slice.call(list.querySelectorAll('[role="tab"]'));
      if (!tabs.length) return;

      function select(index, focus) {
        tabs.forEach(function (tab, i) {
          var on = i === index;
          tab.setAttribute('aria-selected', on ? 'true' : 'false');
          tab.setAttribute('tabindex', on ? '0' : '-1');
          var panel = document.getElementById(tab.getAttribute('aria-controls'));
          if (panel) panel.hidden = !on;
        });
        if (focus) tabs[index].focus();
        // Keep the active chip in view on a narrow, scrolling tab row.
        var active = tabs[index];
        if (list.scrollWidth > list.clientWidth + 4) {
          list.scrollTo({
            left: active.offsetLeft - (list.clientWidth - active.offsetWidth) / 2,
            behavior: 'smooth'
          });
        }
      }

      tabs.forEach(function (tab, i) {
        tab.addEventListener('click', function () { select(i, false); });
        tab.addEventListener('keydown', function (event) {
          var next = null;
          if (event.key === 'ArrowRight') next = (i + 1) % tabs.length;
          else if (event.key === 'ArrowLeft') next = (i - 1 + tabs.length) % tabs.length;
          else if (event.key === 'Home') next = 0;
          else if (event.key === 'End') next = tabs.length - 1;
          if (next !== null) {
            event.preventDefault();
            select(next, true);
          }
        });
      });
    });
  }

  /* ---------------------------------------------------------------------
     Hero banner dots
     --------------------------------------------------------------------- */
  function initBanner() {
    document.querySelectorAll('[data-hk-banner]').forEach(function (banner) {
      if (banner.getAttribute('data-hk-banner-ready') === 'true') return;
      banner.setAttribute('data-hk-banner-ready', 'true');

      var track = banner.querySelector('.hk-banner__track');
      var dots = Array.prototype.slice.call(banner.querySelectorAll('.hk-banner__dot'));
      if (!track || !dots.length) return;

      dots.forEach(function (dot, i) {
        dot.addEventListener('click', function () {
          track.scrollTo({ left: track.clientWidth * i, behavior: 'smooth' });
        });
      });

      var sync = function () {
        var index = track.clientWidth ? Math.round(track.scrollLeft / track.clientWidth) : 0;
        dots.forEach(function (dot, i) {
          dot.setAttribute('aria-current', i === index ? 'true' : 'false');
        });
      };

      track.addEventListener('scroll', function () { window.requestAnimationFrame(sync); }, { passive: true });
      window.addEventListener('resize', sync, { passive: true });
      sync();
    });
  }

  /* ---------------------------------------------------------------------
     Video shelf — play only what is on screen
     Several autoplaying clips at once will stutter on a mid-range phone and
     burn battery, so each one is paused the moment it scrolls away.
     --------------------------------------------------------------------- */
  function initVideoShelf() {
    var videos = document.querySelectorAll('.hk-vshelf__media video');
    if (!videos.length) return;

    if (!('IntersectionObserver' in window)) return;

    var observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          var video = entry.target;
          if (entry.isIntersecting) {
            var attempt = video.play();
            // Autoplay can still be refused (data saver, low power mode).
            // Nothing to recover from — the poster frame stays put.
            if (attempt && typeof attempt.catch === 'function') attempt.catch(function () {});
          } else if (!video.paused) {
            video.pause();
          }
        });
      },
      { threshold: 0.25 }
    );

    videos.forEach(function (video) {
      if (video.getAttribute('data-hk-shelf-ready') === 'true') return;
      video.setAttribute('data-hk-shelf-ready', 'true');
      // Belt and braces: Liquid sets these, but a cached section could predate it.
      video.muted = true;
      video.loop = true;
      video.playsInline = true;
      video.controls = false;
      observer.observe(video);
    });
  }

  /* ---------------------------------------------------------------------
     Wire up
     --------------------------------------------------------------------- */
  function init() {
    document.addEventListener('click', function (event) {
      var add = event.target.closest('[data-hk-quick-add]');
      if (add) {
        event.preventDefault();
        handleQuickAdd(add);
        return;
      }
      var wish = event.target.closest('.hk-wish');
      if (wish) {
        event.preventDefault();
        toggleWishlist(wish);
      }
    });

    syncWishlistButtons();
    initStickyBar();
    initRails();
    initBuyBar();
    initGalleryCounter();
    initTabs();
    initBanner();
    initVideoShelf();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Re-sync after a theme-editor section reload.
  document.addEventListener('shopify:section:load', function () {
    syncWishlistButtons();
    initStickyBar();
    initRails();
    initBuyBar();
    initGalleryCounter();
    initTabs();
    initBanner();
    initVideoShelf();
  });
})();
